import {html, nothing, type TemplateResult} from 'lit';
import {STREAK_CAP, STREAK_MIN, type Milestone} from '../quiz-controller';

export interface ProgressLineProps {
  today: number;
  goal: number;
  mastered: number;
  advanced: number;
  total: number;
  /** 学習した日数（累計）。2 日目から「N 日目」として出す。途切れは数えない */
  days: number;
  panelOpen: boolean;
  streak: number;
  milestone: Milestone | null;
  /** ○ を静止表示している間（モーション低減時など）はチップも動かさない */
  persistent: boolean;
  /** 下端に案内の 1 行が出ているとき、連続正解チップを一段上げて重ねない */
  chipRaised?: boolean;
  onToggle: () => void;
}

/**
 * 出題ボックス（h-64 / overflow-hidden）の内側に絶対配置する常設 UI。
 *
 * 上 24px に「今日 N/目標 問 ・ 定着 N / M」と記録パネルの開閉、下 3px に定着バー、
 * 下中央に節目のチップ、右下に連続正解のチップ。すべて絶対配置なので**初期描画の高さを
 * 1px も変えない**。README のプレースホルダ 3 段はこれに依存している。
 * フローに入れる要素をここに足してはいけない。
 *
 * ボタンは右端の「記録」だけ。帯全体をボタンにすると、出題ボックスの上端を触った
 * だけで 2000px のパネルが開く。数字は読めるテキストとして置く。
 *
 * 数字は毎問読み上げない（live region にしない）。節目は character-display.ts の
 * 既存の role="status" に文として乗せる。
 *
 * 見せるのは学習の事実である量だけ。直近 7 日の点列は初日に空円が 6 個並び
 * 「途切れ」を見せることになるのでここには出さない（パネルの中に残す）。
 */
export function progressLine(p: ProgressLineProps): TemplateResult {
  const pct = (n: number) => (p.total > 0 ? Math.min(100, (100 * n) / p.total) : 0);
  const chip = p.milestone
    ? p.milestone.kind
    : p.streak >= STREAK_MIN
      ? `連続 ${Math.min(p.streak, STREAK_CAP)}`
      : null;
  const isMilestone = p.milestone !== null;
  // 目標に届いたら、1.3 秒のチップで終わらせずにこの行を「達成」の状態にして
  // セッションの区切りを持続させる。実数（今日 46問）は残し、分母だけ落とす
  const reached = p.goal > 0 && p.today >= p.goal;

  return html`
    <div
      class="absolute top-0 inset-x-0 h-6 z-10 flex items-center justify-between gap-2 px-3 font-sans text-xs sm:text-sm font-medium text-gray-700 tabular-nums whitespace-nowrap bg-white/85"
    >
      <span class="min-w-0 truncate"
        >${reached
          ? html`今日 ${p.today}問 ・ <span class="text-teal-800">目標達成</span><span
                class="hidden sm:inline"
                >${' '}・ 定着 ${p.mastered} / ${p.total}</span
              >`
          : html`今日 ${p.today}/${p.goal}問 ・ 定着 ${p.mastered} / ${p.total}`}${p.days >= 2
          ? html`${' '}・ ${p.days} 日目`
          : nothing}</span
      >
      <button
        type="button"
        aria-expanded=${p.panelOpen ? 'true' : 'false'}
        aria-controls="dq-progress-panel"
        aria-label="学習の記録を${p.panelOpen ? '閉じる' : '詳しく見る'}"
        @click=${p.onToggle}
        class="relative shrink-0 inline-flex items-center gap-0.5 text-indigo-700 hover:text-indigo-900 cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 after:absolute after:-inset-x-3 after:top-0 after:h-11 after:content-['']"
      >
        記録
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          ${p.panelOpen ? html`<path d="m18 15-6-6-6 6" />` : html`<path d="m6 9 6 6 6-6" />`}
        </svg>
      </button>
    </div>

    <!-- 節目は ○ と同じ中央の視線上（下中央）に一回り大きく出す。右下の 12px では
         1.3 秒の窓で気づけなかった。連続正解は従来どおり右下に小さく。
         中央寄せは transform ではなく inset-x-0 + mx-auto + w-fit で行う
         （dq-chip のキーフレームが transform を上書きするため） -->
    ${chip
      ? html`<span
          aria-hidden="true"
          class="absolute z-10 font-sans rounded-full ${isMilestone
            ? 'bottom-3 inset-x-0 mx-auto w-fit text-sm px-3 py-1 bg-indigo-600 text-white shadow'
            : `${p.chipRaised ? 'bottom-10' : 'bottom-2'} right-3 text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700`} ${isMilestone &&
          !p.persistent
            ? 'animate-dq-chip'
            : ''}"
          >${chip}</span
        >`
      : nothing}

    <div aria-hidden="true" class="absolute bottom-0 inset-x-0 h-[3px] bg-gray-100">
      <div class="dq-bar absolute inset-y-0 left-0 bg-teal-200" style="width: ${pct(p.advanced)}%"></div>
      <div class="dq-bar absolute inset-y-0 left-0 bg-teal-600" style="width: ${pct(p.mastered)}%"></div>
    </div>
  `;
}
